import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';

import { contactsApi } from '@/api/services/contacts.api';

const CONTACTS_PAGE_SIZE = 30;

interface UseWidgetContactsInput {
    /** Matches first/last name, phone, or email server-side; undefined = whole org. */
    readonly search?: string;
    /** Endpoint requires CONTACTS_READ — gate so the query never 403s. */
    readonly enabled?: boolean;
}

/**
 * Infinite org contact book for the widget's Contacts tab — alphabetical,
 * narrowed by the (debounced) search box. Keyed under the contacts feature's
 * `['contacts', …]` family so contact CRUD invalidations refresh this list
 * too; the extra 'widget-list' segment keeps it from colliding with the
 * contacts page's plain-query cache entries.
 */
export function useWidgetContacts({ search, enabled }: UseWidgetContactsInput) {
    return useInfiniteQuery({
        queryKey: ['contacts', 'widget-list', search ?? ''],
        queryFn: ({ pageParam }) =>
            contactsApi.list({
                ...(search ? { search } : {}),
                limit: CONTACTS_PAGE_SIZE,
                page: pageParam,
                sortBy: 'firstName',
                sortOrder: 'asc',
            }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) =>
            lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
        enabled: enabled ?? true,
        staleTime: 30_000,
        // Keep the previous list on screen while a new search key loads —
        // otherwise every debounced keystroke flashes the skeleton state.
        placeholderData: keepPreviousData,
    });
}
